<template>
  <div class="w-full flex flex-col items-center">
    <AppCtaButton
      v-if="joinButton"
      class="mt-4 text-white text-xl md:text-3xl tracking-wide uppercase font-bold whitespace-nowrap"
      style="text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5)"
      href="/join"
    >
      Get your password now
    </AppCtaButton>

    <div class="py-6">
      <AppSelect
        v-model="dropdownSelection"
        :items="dropdownLinks"
        placeholder="Support &amp; Other Links"
      />
    </div>

    <Divider />

    <nav
      v-if="hubLinks"
      aria-label="Browse"
      class="w-full max-w-7xl flex flex-col gap-2 px-3 xl:px-0 pt-3 text-sm text-[#9a9a9a]"
    >
      <ul class="flex flex-wrap gap-x-4 gap-y-1 font-bold">
        <li v-for="link in browseLinks" :key="`browse-${link.path}`">
          <NuxtLink :to="link.path" class="hover:text-white">
            {{ link.name }}
          </NuxtLink>
        </li>
      </ul>
      <ul v-if="topTags?.length" class="flex flex-wrap gap-x-3 gap-y-1">
        <li v-for="tag in topTags" :key="`footer-tag-${tag.slug}`">
          <NuxtLink :to="`/tags/${tag.slug}`" class="hover:text-white">
            {{ tag.label }}
          </NuxtLink>
        </li>
      </ul>
      <div v-if="sisterSites.length" class="flex flex-wrap gap-x-3 gap-y-1">
        <span id="footer-sister-sites" class="font-bold">More sites:</span>
        <ul aria-labelledby="footer-sister-sites" class="contents">
          <li v-for="site in sisterSites" :key="`sister-${site.url}`">
            <a :href="site.url" class="hover:text-white">{{ site.name }}</a>
          </li>
        </ul>
      </div>
      <ul v-if="reviewLinks.length" class="flex flex-wrap gap-x-3 gap-y-1">
        <li v-for="link in reviewLinks" :key="`review-${link.url}`">
          <a
            :href="link.url"
            target="_blank"
            :rel="link.rel"
            class="hover:text-white"
          >
            {{ link.name }}
          </a>
        </li>
      </ul>
    </nav>

    <div
      class="w-full max-w-7xl flex flex-col justify-start items-start gap-1 p-3 xl:px-0 pb-20 md:pb-3 text-[#6d6d6d]"
    >
      <NuxtLink to="/2257">
        18 U.S.C. § 2257 Record-Keeping Requirements Compliance Statement
      </NuxtLink>
      <NuxtLink to="/content_removal">
        <p>Report Content</p>
      </NuxtLink>
      <p>
        For billing inquiries or to cancel your membership, please visit
        <a href="https://segpay.com" target="_blank">segpay.com</a>
        , or
        <a href="https://epoch.com" target="_blank">epoch.com</a>
        , our authorized sales agents.
      </p>
      <p v-if="currentSite?.description">{{ currentSite.description }}</p>
      <p v-if="currentSite">
        Copyright &copy; {{ new Date().getFullYear() }} {{ currentSite.name }}.
        All rights reserved
      </p>
      <p v-for="(line, index) in filteredFooterLines" :key="index">
        {{ line.text }}
      </p>
      <a
        href="https://www.rtalabel.org/index.html?content=parents"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-2 opacity-45 hover:opacity-80 transition-opacity"
      >
        <!-- White cut of the official black RTA label (rtalabel.org _d
             variant), faded to sit with the grey footer text. -->
        <img
          src="/images/rta-label-white.png"
          width="60"
          height="21"
          alt="RTA: Restricted To Adults"
          loading="lazy"
        />
      </a>
    </div>
  </div>
</template>

<script setup>
  import { blackFridaySite } from "@/utils/blackFridayCopy"
  import { saleMonth } from "@/utils/seasonalSale"

  // eslint-disable-next-line vue/no-reserved-component-names
  defineOptions({ name: "Footer" })

  const props = defineProps({
    joinButton: {
      type: Boolean,
      default: true,
    },
    // Links to the tour's hub pages and largest tags, for crawlers as much as
    // people. Off in the members area, which has its own navigation.
    hubLinks: {
      type: Boolean,
      default: true,
    },
  })

  const currentSite = inject("currentSite")
  const { hasSeries, hasSubSites } = useSiteSettings(currentSite.value)

  const browseLinks = computed(() =>
    [
      { name: "Videos", path: "/" },
      { name: "Top Rated", path: "/top-rated" },
      currentSite.value?.showModels !== false && {
        name: "Models",
        path: "/models",
      },
      { name: "Tags", path: "/tags" },
      hasSubSites && { name: "Sites", path: "/sites" },
      hasSeries && { name: "Series", path: "/series" },
      // Only in November, on the sites with a Black Friday page.
      blackFridaySite((currentSite.value?.alias ?? currentSite.value)?.host) &&
        saleMonth(new Date()) === 11 && {
          name: "Black Friday Sale",
          path: "/black-friday",
        },
      { name: "Site map", path: "/sitemap" },
    ].filter(Boolean),
  )

  // Review-site links from the site's config. An `only_first_page` link shows
  // on the homepage only; a `follow` link is followed there and nowhere else.
  const route = useRoute()
  const reviewLinks = computed(() => {
    const onHomepage =
      route.path === "/" && (parseInt(route.query.page) || 1) === 1

    return (currentSite.value?.reviewLinks ?? [])
      .filter((link) => link?.url && (onHomepage || !link.only_first_page))
      .map((link) => ({
        name: link.name || link.url,
        url: link.url,
        rel: onHomepage && link.follow ? "noopener" : "nofollow noopener",
      }))
  })

  // The other live tour sites in this site's network, from /api/sites.
  // Followed links: same company, same models.
  const requestUrl = useRequestURL().href
  const sisterSites = computed(() =>
    (currentSite.value?.sisterSites ?? [])
      .map((site) => ({
        name: site.name,
        url: sisterSiteUrl(requestUrl, site.host),
      }))
      .filter((site) => site.name && site.url),
  )

  // The site's 15 largest tags. Skipped where the links aren't shown.
  const { data: topTags } = props.hubLinks
    ? await useApi("/api/tags", { query: { top: 15 } })
    : { data: ref([]) }

  const geoCountry = inject("geoCountry")
  const inAgeRestrictedLocation = inject("inAgeRestrictedLocation")
  const filteredFooterLines = computed(() => {
    return currentSite.value.footerLines.filter((line) => {
      // For US viewers in an age-restricted region, skip lines mentioning "las vegas"
      if (["US"].includes(geoCountry) && inAgeRestrictedLocation) {
        if (/las vegas/i.test(line.text)) {
          return false
        }
      }
      // For GB or UK viewers in an age-restricted region, skip lines mentioning "england"
      if (["GB", "UK"].includes(geoCountry) && inAgeRestrictedLocation) {
        if (/england/i.test(line.text)) {
          return false
        }
      }
      return true
    })
  })

  const dropdownSelection = ref("")
  const dropdownLinks = [
    {
      label: "Billing Support",
      value: "https://cvvhelp.net",
    },
    {
      label: "Epoch.com",
      value: "https://epoch.com",
    },
    {
      label: "SegPay.com",
      value: "https://cs.segpay.com",
    },
    {
      label: "CCBill Billing Support",
      value: "https://support.ccbill.com",
    },
    {
      label: "WTSeTicket.com Support",
      value: "https://wtseticket.com",
    },
    {
      label: "Webmasters",
      value: "https://fuckyoucash.com",
    },
    {
      label: "Contact Us",
      value: "mailto:cvv@cvvhelp.net",
    },
    {
      label: "Terms & Conditions",
      value: "/terms",
    },
    {
      label: "Privacy Policy",
      value: "/privacy",
    },
    {
      label: "Report Content",
      value: "/content_removal",
    },
  ]

  const handleSelection = () => {
    if (dropdownSelection.value) {
      window.location.href = dropdownSelection.value
    }
  }

  watch(() => dropdownSelection.value, handleSelection)
</script>
